import { PlusOutlined } from '@ant-design/icons';
import { useManageConnection } from '../KlaviyoIntegrations/use-manage-connection';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import ConnectionModel, {
  BrazeAccessToken,
} from '@mm/state/model/ConnectionModel';
import { selectBrazeConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as CustomersAiLogo } from 'assets/icons/customersai-logo.svg';
import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './BrazeIntegrations.module.css';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { BrazeConnectionEditor } from './BrazeConnectionEditor';
import { ContactsSync } from '../ContactsSync/ContactsSync';

type Props = {
  accessToken: BrazeAccessToken;
  controlsDisabled: boolean;
};

export const BrazeSyncSetup = (props: Props) => {
  const { accessToken, controlsDisabled } = props;
  const availableTags = useSelector((state: StoreState) =>
    tagsOfKinds(state, ['regular']),
  );
  const audiences = useSelector(selectAudienceSorted);
  const brazeConnections = useSelector(selectBrazeConnections);
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { getAll } = useTagsCRUDActions();

  const { companyName = 'Customers.ai', isWhitelabelCompany } =
    useWhitelabelConfig();
  const connectionTemplate = useCallback(
    (extra: Partial<ConnectionModel> = {}): Partial<ConnectionModel> => ({
      active: true,
      name: 'Braze Connection',
      connectionKind: 'braze',
      brazeParams: {
        accessTokenId: accessToken.id,
      },
      ...extra,
    }),
    [accessToken],
  );

  const validConnection = (
    isAllContactsSync: boolean,
    connection: Partial<ConnectionModel>,
  ) =>
    Boolean(isAllContactsSync ? !connection.audienceId : connection.audienceId);

  useEffect(() => {
    getAll(activeFbPageId, true);
  }, []);

  const {
    allConnections,
    isAllContactsSync,
    selectableAudiences,
    addConnection,
    remove,
    updateOrEdit,
    onChangeType,
  } = useManageConnection({
    connections: brazeConnections,
    audiences,
    lists: [],
    validConnection,
    connectionTemplate,
  });

  const canAdd = selectableAudiences().length;
  const multipleConnections = allConnections.length > 2;

  return (
    <ContactsSync
      isReadOnly={controlsDisabled}
      allConnections={allConnections as ConnectionModel[]}
      connection={allConnections[0] as ConnectionModel}
      onRadioChange={onChangeType}
      name="Braze"
    >
      <div className={styles.gridContainer}>
        {!isAllContactsSync ? (
          <>
            <div className={styles.gridAudiences}>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                {!isWhitelabelCompany && (
                  <CustomersAiLogo className={styles.gridIcon} />
                )}{' '}
                {companyName} Audience
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Add Tag to contacts in Braze
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Actions
              </div>
              {allConnections.map((conn, idx) => {
                if (!conn.id || conn.audienceId) {
                  const key = `connection-${idx}`;
                  return (
                    <BrazeConnectionEditor
                      key={key}
                      canDelete={multipleConnections}
                      connection={conn}
                      withAudiences={true}
                      audiences={selectableAudiences(conn)}
                      update={props => updateOrEdit(idx, props)}
                      remove={() => remove(idx)}
                      activeFbPageId={activeFbPageId}
                      availableTags={availableTags}
                    />
                  );
                }
                return null;
              })}
            </div>
            {canAdd ? (
              <PrimaryButton
                className={classNames(
                  'integrations__header-add-button-secondary',
                  'integrations__header-add-button-action',
                  'integrations__header-button-no-margin',
                )}
                onClick={addConnection}
                leftIcon={<PlusOutlined />}
              >
                Add
              </PrimaryButton>
            ) : null}
          </>
        ) : (
          <div className={styles.gridAll}>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Add Tag to contacts in Braze
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Actions
            </div>
            {allConnections.map((conn, idx) => {
              const key = `connection-${idx}`;
              if (conn.id && !conn.audienceId) {
                return (
                  <BrazeConnectionEditor
                    key={key}
                    canDelete={false}
                    connection={conn}
                    withAudiences={false}
                    audiences={audiences}
                    update={props => updateOrEdit(idx, props)}
                    activeFbPageId={activeFbPageId}
                    availableTags={availableTags}
                  />
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
    </ContactsSync>
  );
};
